import { keepPreviousData, useQuery } from '@tanstack/react-query'
import { useMemo } from 'react'
import type { Product } from '@/api/types'
import type { NoStockReason } from '@/shared/lib/stock-kind'
import { productsApi } from '@/api/endpoints/products'
import { noStockReason } from '@/shared/lib/stock-kind'

/**
 * What a stock input needs to know about an article (CHG-2026-09-stock-usage-and-price-twins, D9,
 * D11): whether it may take decimals ("Teilmengen zählen"), its base unit as shown, and whether it
 * holds stock at all.
 */
export interface StockArticleInfo {
  fractional: boolean
  /** The base unit as shown; "Stk" unless a display name is set. */
  unit: string
  noStock: NoStockReason | null
}

export const DEFAULT_STOCK_UNIT = 'Stk'

export function stockInfoOf(
  product: Partial<
    Pick<
      Product,
      | 'fractionalCounting'
      | 'stockUnit'
      | 'isDish'
      | 'variantSourceSku'
      | 'stockTracked'
      | 'stockKind'
    >
  >,
): StockArticleInfo {
  return {
    fractional: product.fractionalCounting === true,
    unit: product.stockUnit ?? DEFAULT_STOCK_UNIT,
    noStock: noStockReason(product),
  }
}

/**
 * The stock facts a row or picked product already carries (inventory list rows, product list
 * items), or null when it predates them — then, and only then, {@link useStockInfo} asks.
 */
export function stockInfoFromFields(
  fields: Parameters<typeof stockInfoOf>[0],
): StockArticleInfo | null {
  return fields.fractionalCounting === undefined ? null : stockInfoOf(fields)
}

/**
 * The stock facts of a set of SKUs, read with one `GET /products/lookup` (no stock, no restaurant).
 * Only for SKUs whose screen data carries no counting mode (a menu row, a preset movement line, a
 * backend that predates phase B4); pass an empty list and nothing is requested.
 *
 * Unknown — still loading, refused, or a SKU the lookup did not return — means an ordinary
 * whole-number article: every input then behaves exactly as it did before decimals existed, and
 * the server refuses a decimal for a whole article anyway. The previous answer is kept while the
 * SKU set grows, so adding a line does not flip the other lines back to whole numbers.
 */
export function useStockInfo(
  skus: Array<string>,
): Map<string, StockArticleInfo> {
  const key = useMemo(() => [...new Set(skus)].sort(), [skus])
  const { data } = useQuery({
    queryKey: ['products', 'lookup', 'stock-info', key],
    queryFn: () => productsApi.lookup(key),
    enabled: key.length > 0,
    placeholderData: keepPreviousData,
    staleTime: 60_000,
    // A 403 (inventory rights without product:view) or a 5xx is answered once: the inputs then
    // stay whole-number, which is what the server accepts for an unknown article anyway.
    retry: false,
  })
  return useMemo(() => {
    const map = new Map<string, StockArticleInfo>()
    for (const product of data?.items ?? []) {
      map.set(product.sku, stockInfoOf(product))
    }
    return map
  }, [data])
}
